import React from 'react';
import { Code, Search, FileText, Rocket } from 'lucide-react';
import { useInView } from '../../hooks/useInView';
import { processSteps } from '../../data/mock';

const iconMap = { Code, Search, FileText, Rocket };

const StepCard = ({ step, index, total }) => {
  const [ref, isInView] = useInView();
  const Icon = iconMap[step.iconName];

  return (
    <div
      ref={ref}
      className={`relative flex flex-col items-center text-center transition-[opacity,transform] duration-600 ease-out ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {/* Connector line (not on last) */}
      {index < total - 1 && (
        <div className="hidden lg:block absolute top-8 left-[calc(50%+40px)] w-[calc(100%-80px)] h-px bg-stone-300/60" />
      )}

      {/* Step circle */}
      <div className="relative w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200/60 flex items-center justify-center mb-6 transition-[background-color,transform] duration-300 hover:bg-amber-100 hover:scale-105">
        {Icon && <Icon className="w-7 h-7 text-amber-600" />}
        <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-stone-900 text-white text-xs font-bold flex items-center justify-center">
          {step.step}
        </span>
      </div>

      <h3 className="text-lg font-semibold text-stone-900 mb-2">{step.title}</h3>
      <p className="text-sm text-stone-500 leading-relaxed max-w-xs mb-3">
        {step.description}
      </p>
      <p className="text-xs text-stone-400 font-medium font-mono bg-stone-100 px-3 py-1.5 rounded-full">
        {step.details}
      </p>
    </div>
  );
};

export const HowItWorksSection = () => {
  const [titleRef, titleInView] = useInView();

  return (
    <section
      id="how-it-works"
      className="py-24 lg:py-32 bg-stone-50/80"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={titleRef}
          className={`text-center mb-20 transition-[opacity,transform] duration-700 ease-out ${
            titleInView
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="text-xs font-semibold tracking-widest uppercase text-amber-600 mb-3 block">
            Simple Process
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
            How It Works
          </h2>
          <p className="text-stone-500 max-w-2xl mx-auto text-lg">
            From sharing your code to shipping improvements — a streamlined
            process designed around your workflow.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {processSteps.map((step, i) => (
            <StepCard
              key={step.step}
              step={step}
              index={i}
              total={processSteps.length}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
